"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";
import { Bar, BarChart, CartesianGrid, XAxis } from "recharts";
import { CircleCheck, TrendingUp, Lightbulb, ArrowRight, Target } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const chartData = [{
  month: "Q1",
  efficiency: 65
}, {
  month: "Q2",
  efficiency: 78
}, {
  month: "Q3",
  efficiency: 88
}, {
  month: "Q4",
  efficiency: 100
}];
const chartConfig = {
  efficiency: {
    label: "Эффективность",
    color: "var(--chart-1)"
  }
} satisfies ChartConfig;
export default function About() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="about" ref={ref} className={`relative w-full py-16 lg:py-24 bg-slate-50 overflow-hidden transition-opacity duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {}
          <div className="hidden lg:flex flex-col justify-start pt-8">
            <div className="sticky top-24 flex items-center justify-center w-12 h-auto">
              <span className="text-sm font-bold tracking-widest text-muted-foreground uppercase [writing-mode:vertical-rl] rotate-180 whitespace-nowrap">
                О Компании
              </span>
            </div>
          </div>

          {}
          <div className="lg:hidden mb-4">
            <span className="text-sm font-bold tracking-widest text-primary uppercase">
              О Компании
            </span>
            <h2 className="text-3xl font-bold mt-2 text-foreground">
              МерчЭксперт
            </h2>
          </div>

          {}
          <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 auto-rows-min">
            
            {}
            <Card className="md:col-span-2 md:row-span-1 bg-card border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300 flex flex-col justify-between">
              <CardHeader>
                <div className="flex items-center gap-2 mb-2">
                  <Target className="w-5 h-5 text-primary" />
                  <span className="text-xs font-semibold text-primary uppercase tracking-wider">
                    Наша Миссия
                  </span>
                </div>
                <CardTitle className="text-2xl md:text-3xl font-bold leading-tight">
                  Стратегии, которые продают
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-muted-foreground leading-relaxed">Агенство ПРОФИ – это команда профессионалов, объединяющая аналитический подход и креативные решения. Мы специализируемся на мерчандайзинге и организации промо‑мероприятий, превращая торговые площади в эффективные инструменты продаж. Наша цель — повысить узнаваемость вашего бренда и укрепить лояльность покупателей через безупречное визуальное представление.</p>
                <div className="flex flex-wrap gap-4">
                  <Button asChild className="rounded-full px-6">
                    <Link to="/services">
                      Наши Услуги <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </Button>
                  <Button variant="outline" asChild className="rounded-full px-6">
                    <Link to="#contact">Связаться с нами</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {}
            <Card className="md:col-span-1 bg-card border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300 flex flex-col">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-chart-2" />
                    <span className="text-xs font-semibold text-muted-foreground uppercase">
                      Аналитика
                    </span>
                  </div>
                </div>
                <CardTitle className="text-lg">Рост Продаж</CardTitle>
                <CardDescription>Эффективность кампаний</CardDescription>
              </CardHeader>
              <CardContent className="flex-1 flex items-end pb-4">
                <ChartContainer config={chartConfig} className="w-full h-[140px]">
                  <BarChart data={chartData} margin={{
                  top: 0,
                  bottom: 0
                }}>
                    <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="month" tickLine={false} tickMargin={10} axisLine={false} tickFormatter={value => value.slice(0, 3)} className="text-xs text-muted-foreground" />
                    <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
                    <Bar dataKey="efficiency" fill="var(--color-efficiency)" radius={[4, 4, 0, 0]} barSize={32} />
                  </BarChart>
                </ChartContainer>
              </CardContent>
            </Card>

            {}
            <Card className="md:col-span-1 bg-card border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden group relative min-h-[240px]">
              <div className="absolute inset-0 bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1567030/img/retail_creative_strategy.png)] transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <CardHeader className="relative z-10 h-full flex flex-col justify-end pb-6">
                <div className="flex items-center gap-2 mb-1">
                  <Lightbulb className="w-5 h-5 text-yellow-400" />
                  <span className="text-xs font-semibold text-white/90 uppercase">
                    Креатив
                  </span>
                </div>
                <CardTitle className="text-white text-xl">
                  Визуальная Стратегия
                </CardTitle>
                <CardDescription className="text-white/80">
                  Яркие решения для полок
                </CardDescription>
              </CardHeader>
            </Card>

            {}
            <Card className="md:col-span-2 bg-card border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300">
              <CardHeader>
                <div className="flex items-center gap-2 mb-2">
                  <CircleCheck className="w-5 h-5 text-green-500" />
                  <span className="text-xs font-semibold text-muted-foreground uppercase">
                    Контроль Качества
                  </span>
                </div>
                <CardTitle className="text-xl">Почему выбирают нас</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {["Индивидуальный подход к клиенту", "Опыт работы с крупными сетями", "Оперативное выполнение задач", "Пост-активный анализ результатов"].map((item, i) => <div key={i} data-index={i} className="flex items-start gap-3">
                      <div className="mt-1 w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                      <span className="text-sm text-muted-foreground font-medium">
                        {item}
                      </span>
                    </div>)}
                </div>
              </CardContent>
            </Card>

          </div>
        </div>
      </div>
    </section>;
}